// src/pages/BlogPosts.jsx
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  Modal,
  ModalHeader,
  Form,
  ModalBody,
  Label,
  Input,
  FormFeedback,
  Button,
} from 'reactstrap';
import moment from 'moment';
import 'moment/locale/fa';
import { Link } from 'react-router-dom';
import classnames from 'classnames';
import BreadCrumb from '../Components/Common/BreadCrumb';
import TableContainer from '../Components/Common/TableContainer';
import DeleteModal from '../Components/Common/DeleteModal';
import ExportCSVModal from '../Components/Common/ExportCSVModal';
import { BlogPostContext } from '../context/BlogPostContext';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import EditorText from '../Components/Common/EditorText';

// تابع برای حذف تگ‌های HTML و محاسبه طول متن خالص
const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

// نگاشت مقادیر وضعیت
const mapStatus = (status) => {
  switch (String(status)) {
    case '1':
    case 'Published':
      return 'Published';
    case '2':
    case 'Draft':
      return 'Draft';
    case '3':
    case 'Archived':
      return 'Archived';
    default:
      return 'Draft';
  }
};

const BlogPosts = () => {
  document.title = 'پست‌های بلاگ | فات 90';

  const { posts, categories, loading, error, fetchPosts, fetchCategories, addPost, editPost, removePost } =
    useContext(BlogPostContext);
  const [post, setPost] = useState(null);
  const [modal, setModal] = useState(false);
  const [exportModal, setExportModal] = useState(false);
  const [activeTab, setActiveTab] = useState('1');
  const [isEdit, setIsEdit] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [selectedCheckBoxDelete, setSelectedCheckBoxDelete] = useState([]);
  const [isMultiDeleteButton, setIsMultiDeleteButton] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  // گزینه‌های نوع پست
  const typePostOptions = [
    { value: '', label: 'انتخاب کنید' },
    { value: 'هیچکدام', label: 'هیچکدام' },
    { value: 'پربحث', label: 'پربحث' },
    { value: 'محبوب', label: 'محبوب' },
  ];

  // تعداد پست‌ها برای هر تب
  const postCounts = useMemo(() => ({
    all: posts.length,
    published: posts.filter((p) => p.status === 'Published').length,
    draft: posts.filter((p) => p.status === 'Draft').length,
    archived: posts.filter((p) => p.status === 'Archived').length,
  }), [posts]);

  // ستون‌های خروجی
  const exportColumns = [
    { header: 'شناسه', accessorKey: 'id' },
    { header: 'عنوان', accessorKey: 'title' },
    { header: 'دسته‌بندی', accessorKey: 'categoryName' },
    { header: 'نویسنده', accessorKey: 'author' },
    { header: 'نوع پست', accessorKey: 'typePost' },
    {
      header: 'تاریخ انتشار',
      accessorKey: 'publishedAt',
      cell: (value) => moment(value).locale('fa').format('DD MMM YYYY, HH:mm'),
    },
    {
      header: 'وضعیت',
      accessorKey: 'status',
      cell: (value) => {
        switch (value) {
          case 'Published':
            return 'منتشرشده';
          case 'Draft':
            return 'پیش‌نویس';
          case 'Archived':
            return 'آرشیو';
          default:
            return value;
        }
      },
    },
    {
      header: 'تعداد نظرات',
      accessorKey: 'commentCount',
      cell: (value) => (value != null ? value.toLocaleString('fa-IR') : '0'),
    },
  ];

  // مقادیر اولیه فرم
  const formInitialValues = {
    title: '',
    categoryId: '',
    content: '',
    image: null,
    metaDescription: '',
    metaTitle: '',
    slug: '',
    author: '',
    typePost: '',
    status: 'Draft',
    isEdit: false,
  };

  // اعتبارسنجی فرم
  const validation = useFormik({
    enableReinitialize: true,
    initialValues: post ? { ...post, isEdit } : formInitialValues,
    validationSchema: Yup.object().shape({
      title: Yup.string()
        .required('لطفاً عنوان را وارد کنید')
        .max(250, 'عنوان باید حداکثر 250 کاراکتر باشد'),
      categoryId: Yup.number()
        .required('لطفاً دسته‌بندی را انتخاب کنید')
        .min(1, 'لطفاً یک دسته‌بندی معتبر انتخاب کنید'),
      content: Yup.string()
        .required('لطفاً محتوا را وارد کنید')
        .test('min-length', 'محتوا باید حداقل 300 کاراکتر باشد', (value) => {
          const text = stripHtml(value || '');
          return text.length >= 300;
        }),
      image: Yup.mixed()
        .nullable()
        .test('image-required', 'لطفاً یک تصویر انتخاب کنید', function (value) {
          const { isEdit } = this.parent;
          if (!isEdit && value == null) {
            return false;
          }
          return true;
        })
        .test('image-format', 'فقط فرمت‌های PNG، JPG، یا WebP مجاز هستند', function (value) {
          if (value) {
            return ['image/png', 'image/jpeg', 'image/webp'].includes(value.type);
          }
          return true;
        })
        .test('image-size', 'حجم تصویر باید کمتر از 300KB باشد', function (value) {
          if (value) {
            return value.size <= 307200;
          }
          return true;
        }),
      metaDescription: Yup.string()
        .required('لطفاً توضیحات متا را وارد کنید')
        .max(160, 'توضیحات متا باید حداکثر 160 کاراکتر باشد')
        .min(100, 'توضیحات متا باید حداقل 100 کاراکتر باشد'),
      metaTitle: Yup.string()
        .required('لطفاً عنوان متا را وارد کنید')
        .max(60, 'عنوان متا باید حداکثر 60 کاراکتر باشد')
        .min(20, 'عنوان متا باید حداقل 20 کاراکتر باشد'),
      slug: Yup.string()
        .required('لطفاً slug را وارد کنید')
        .max(250, 'Slug باید حداکثر 250 کاراکتر باشد')
        .matches(/^[a-z0-9-]+$/, 'Slug فقط شامل حروف انگلیسی کوچک، اعداد، و خط تیره باشد'),
      author: Yup.string()
        .required('لطفاً نویسنده را وارد کنید')
        .max(60, 'نویسنده باید حداکثر 60 کاراکتر باشد'),
      typePost: Yup.string()
        .required('لطفاً نوع پست را انتخاب کنید')
        .oneOf(['هیچکدام', 'پربحث', 'محبوب'], 'لطفاً یک نوع پست معتبر انتخاب کنید'),
      status: Yup.string()
        .required('لطفاً وضعیت را انتخاب کنید')
        .oneOf(['Draft', 'Published', 'Archived'], 'وضعیت نامعتبر است'),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append('title', values.title);
        formData.append('categoryId', values.categoryId);
        formData.append('content', values.content);
        if (values.image) {
          formData.append('image', values.image);
        }
        formData.append('metaDescription', values.metaDescription);
        formData.append('metaTitle', values.metaTitle);
        formData.append('slug', values.slug);
        formData.append('author', values.author);
        formData.append('typePost', values.typePost);
        formData.append('status', values.status);

        if (isEdit) {
          await editPost(post.id, formData);
          toast.success('پست با موفقیت به‌روزرسانی شد');
        } else {
          await addPost(formData);
          toast.success('پست با موفقیت اضافه شد');
        }
        toggle();
        validation.resetForm();
        setImagePreview(null);
      } catch (err) {
        toast.error('خطایی رخ داد. لطفاً دوباره تلاش کنید.');
      }
    },
  });

  // مدیریت مودال اصلی
  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setPost(null);
      setIsEdit(false);
      setImagePreview(null);
      validation.resetForm();
    } else {
      setModal(true);
    }
  }, [modal, validation]);

  // مدیریت مودال خروجی
  const toggleExportModal = () => {
    setExportModal(!exportModal);
  };

  // افزودن پست جدید
  const handlePostClicks = () => {
    setPost(null);
    setIsEdit(false);
    setImagePreview(null);
    validation.resetForm();
    toggle();
  };

  // ویرایش پست
  const handlePostClick = useCallback(
    (postData) => {
      const postValues = {
        id: postData.id,
        title: postData.title || '',
        categoryId: postData.categoryId || '',
        content: postData.content || '',
        image: null,
        imagePath: postData.imagePath || '',
        metaDescription: postData.metaDescription || '',
        metaTitle: postData.metaTitle || '',
        slug: postData.slug || '',
        author: postData.author || '',
        typePost: postData.typePost || '',
        status: mapStatus(postData.status),
        isEdit: true,
      };
      setPost(postValues);
      validation.setValues(postValues); // تنظیم مقادیر فرم
      setImagePreview(postData.imagePath ? `${process.env.REACT_APP_API_BASE_URL}${postData.imagePath}` : null);
      setIsEdit(true);
      toggle();
    },
    [toggle, validation]
  );

  // حذف پست
  const onClickDelete = (postData) => {
    setPost(postData);
    setDeleteModal(true);
  };

  const handleDeletePost = async () => {
    if (post) {
      try {
        await removePost(post.id);
        setDeleteModal(false);
        toast.success('پست با موفقیت حذف شد');
      } catch (err) {
        toast.error('خطایی در حذف پست رخ داد');
      }
    }
  };

  // انتخاب همه چک‌باکس‌ها
  const checkedAll = useCallback(() => {
    const checkall = document.getElementById('checkBoxAll');
    const ele = document.querySelectorAll('.postCheckBox');
    const newSelected = [];
    if (checkall.checked) {
      ele.forEach((el) => {
        el.checked = true;
        newSelected.push(el);
      });
    } else {
      ele.forEach((el) => {
        el.checked = false;
      });
    }
    setSelectedCheckBoxDelete(newSelected);
    setIsMultiDeleteButton(newSelected.length > 0);
  }, []);

  // مدیریت انتخاب چک‌باکس‌ها
  const deleteCheckbox = () => {
    const ele = document.querySelectorAll('.postCheckBox:checked');
    setSelectedCheckBoxDelete(ele);
    setIsMultiDeleteButton(ele.length > 0);
  };

  // حذف گروهی
  const deleteMultiple = async () => {
    const checkall = document.getElementById('checkBoxAll');
    try {
      for (const element of selectedCheckBoxDelete) {
        await removePost(parseInt(element.value));
      }
      setIsMultiDeleteButton(false);
      checkall.checked = false;
      setDeleteModalMulti(false);
      toast.success('پست‌های انتخاب‌شده با موفقیت حذف شدند');
    } catch (err) {
      toast.error('خطایی در حذف گروهی رخ داد');
    }
  };

  // فیلتر بر اساس وضعیت
  const toggleTab = (tab, type) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      let filteredPosts = posts;
      if (type !== 'all') {
        filteredPosts = posts.filter((post) => post.status === type);
      }
      setPostList(filteredPosts);
    }
  };

  // مدیریت آپلود تصویر
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validation.setFieldValue('image', file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  // دریافت پست‌ها و دسته‌بندی‌ها
  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, [fetchPosts, fetchCategories]);

  // نگاشت پست‌ها و به‌روزرسانی لیست
  const [postList, setPostList] = useState([]);
  useEffect(() => {
    const mappedPosts = posts.map((post) => ({
      ...post,
      status: mapStatus(post.status),
      commentCount: post.commentCount ?? 0,
    }));
    setPostList(activeTab === '1' ? mappedPosts : mappedPosts.filter((post) => {
      if (activeTab === '2') return post.status === 'Published';
      if (activeTab === '3') return post.status === 'Draft';
      if (activeTab === '4') return post.status === 'Archived';
      return true;
    }));
  }, [posts, activeTab]);

  // ستون‌های جدول
  const columns = useMemo(
    () => [
      {
        header: (
          <input
            type="checkbox"
            id="checkBoxAll"
            className="form-check-input"
            onChange={checkedAll}
          />
        ),
        cell: (cell) => (
          <input
            type="checkbox"
            className="postCheckBox form-check-input"
            value={cell.getValue()}
            onChange={deleteCheckbox}
          />
        ),
        id: '#',
        accessorKey: 'id',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: 'عنوان',
        accessorKey: 'title',
        enableColumnFilter: false,
        cell: (cell) => (
          <a
            href={`${process.env.REACT_APP_BLOG_BASE_URL}${process.env.REACT_APP_BLOG_PATH}/${cell.row.original.slug}`}
            target="_blank"
            rel="noopener noreferrer"
            className="fw-medium link-primary"
          >
            {cell.getValue()}
          </a>
        ),
      },
      {
        header: 'دسته‌بندی',
        accessorKey: 'categoryName',
        enableColumnFilter: false,
      },
      {
        header: 'نویسنده',
        accessorKey: 'author',
        enableColumnFilter: false,
      },
      {
        header: 'نوع پست',
        accessorKey: 'typePost',
        enableColumnFilter: false,
      },
      {
        header: 'تاریخ انتشار',
        accessorKey: 'publishedAt',
        enableColumnFilter: false,
        cell: (cell) => moment(cell.getValue()).locale('fa').format('DD MMM YYYY, HH:mm'),
      },
      {
        header: 'وضعیت',
        accessorKey: 'status',
        enableColumnFilter: false,
        cell: (cell) => {
          switch (cell.getValue()) {
            case 'Draft':
              return <span className="badge text-uppercase bg-warning-subtle text-warning">پیش‌نویس</span>;
            case 'Published':
              return <span className="badge text-uppercase bg-success-subtle text-success">منتشرشده</span>;
            case 'Archived':
              return <span className="badge text-uppercase bg-danger-subtle text-danger">آرشیو</span>;
            default:
              return <span className="badge text-uppercase bg-secondary-subtle text-secondary">{cell.getValue()}</span>;
          }
        },
      },
      {
        header: 'تعداد نظرات',
        accessorKey: 'commentCount',
        enableColumnFilter: false,
        cell: (cell) => (cell.getValue() != null ? cell.getValue().toLocaleString('fa-IR') : '0'),
      },
      {
        header: 'عملیات',
        cell: (cellProps) => (
          <ul className="list-inline hstack gap-2 mb-0">
            <li className="list-inline-item">
              <a
                href={`${process.env.REACT_APP_BLOG_BASE_URL}${process.env.REACT_APP_BLOG_PATH}/${cellProps.row.original.slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary d-inline-block"
              >
                <i className="ri-eye-fill fs-16"></i>
              </a>
            </li>
            <li className="list-inline-item edit">
              <Link
                to="#"
                className="text-primary d-inline-block edit-item-btn"
                onClick={() => handlePostClick(cellProps.row.original)}
              >
                <i className="ri-pencil-fill fs-16"></i>
              </Link>
            </li>
            <li className="list-inline-item">
              <Link
                to="#"
                className="text-danger d-inline-block remove-item-btn"
                onClick={() => onClickDelete(cellProps.row.original)}
              >
                <i className="ri-delete-bin-5-fill fs-16"></i>
              </Link>
            </li>
          </ul>
        ),
      },
    ],
    [checkedAll, handlePostClick]
  );

  return (
    <div className="page-content" dir="rtl">
      <ToastContainer closeButton={false} limit={1} />
      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDeletePost}
        onCloseClick={() => setDeleteModal(false)}
      />
      <DeleteModal
        show={deleteModalMulti}
        onDeleteClick={() => {
          deleteMultiple();
          setDeleteModalMulti(false);
        }}
        onCloseClick={() => setDeleteModalMulti(false)}
      />
      <ExportCSVModal
        show={exportModal}
        onCloseClick={toggleExportModal}
        data={postList}
        columns={exportColumns}
        fileName="پست‌های_بلاگ"
      />
      <Container fluid>
        <BreadCrumb title="پست‌های بلاگ" pageTitle="مدیریت" />
        <Row>
          <Col lg={12}>
            <Card id="postList">
              <CardHeader className="card-header border-0">
                <Row className="align-items-center gy-3">
                  <div className="col-sm">
                    <h5 className="card-title mb-0">لیست پست‌های بلاگ</h5>
                  </div>
                  <div className="col-sm-auto">
                    <div className="d-flex gap-1 flex-wrap">
                      <Button color="primary" className="add-btn" onClick={handlePostClicks}>
                        <i className="ri-add-line align-bottom me-1"></i> افزودن پست
                      </Button>
                      {isMultiDeleteButton && (
                        <Button color="soft-danger" onClick={() => setDeleteModalMulti(true)}>
                          <i className="ri-delete-bin-2-line"></i>
                        </Button>
                      )}
                      <Button color="soft-info" onClick={toggleExportModal}>
                        <i className="ri-file-download-line align-bottom me-1"></i> خروجی
                      </Button>
                    </div>
                  </div>
                </Row>
              </CardHeader>
              <CardBody className="pt-0">
                <div>
                  <Nav className="nav-tabs nav-tabs-custom nav-primary" role="tablist">
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '1' })}
                        onClick={() => toggleTab('1', 'all')}
                        href="#"
                      >
                        <i className="ri-store-2-fill me-1 align-bottom"></i> همه پست‌ها ({postCounts.all.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '2' })}
                        onClick={() => toggleTab('2', 'Published')}
                        href="#"
                      >
                        <i className="ri-checkbox-circle-line me-1 align-bottom"></i> منتشرشده ({postCounts.published.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '3' })}
                        onClick={() => toggleTab('3', 'Draft')}
                        href="#"
                      >
                        <i className="ri-file-draft-line me-1 align-bottom"></i> پیش‌نویس ({postCounts.draft.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={classnames({ active: activeTab === '4' })}
                        onClick={() => toggleTab('4', 'Archived')}
                        href="#"
                      >
                        <i className="ri-archive-line me-1 align-bottom"></i> آرشیو ({postCounts.archived.toLocaleString('fa-IR')})
                      </NavLink>
                    </NavItem>
                  </Nav>
                  {loading ? (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">در حال بارگذاری...</h5>
                    </div>
                  ) : error ? (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">{error}</h5>
                    </div>
                  ) : postList.length ? (
                    <TableContainer
                      columns={columns}
                      data={postList || []}
                      customPageSize={8}
                      divClass="table-responsive table-card mb-1"
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light text-muted"
                      SearchPlaceholder="جستجو بر اساس عنوان، نویسنده یا دسته‌بندی..."
                      showPagination={true}
                    />
                  ) : (
                    <div className="py-4 text-center">
                      <lord-icon
                        src="https://cdn.lordicon.com/msoeawqm.json"
                        trigger="loop"
                        colors="primary:#25a0e2,secondary:#00bd9d"
                        style={{ width: '72px', height: '72px' }}
                      ></lord-icon>
                      <h5 className="mt-4">هیچ پستی یافت نشد</h5>
                    </div>
                  )}
                </div>
                <Modal id="showModal" isOpen={modal} toggle={toggle} centered size="xl">
                  <ModalHeader className="bg-light p-3" toggle={toggle}>
                    {isEdit ? 'ویرایش پست' : 'افزودن پست'}
                  </ModalHeader>
                  <Form
                    className="tablelist-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      validation.handleSubmit();
                      return false;
                    }}
                    dir="rtl"
                  >
                    <ModalBody>
                      <Row>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="title-field" className="form-label">عنوان</Label>
                            <Input
                              name="title"
                              id="title-field"
                              className="form-control"
                              placeholder="عنوان پست را وارد کنید"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.title || ''}
                              invalid={validation.touched.title && !!validation.errors.title}
                            />
                            <FormFeedback>{validation.errors.title}</FormFeedback>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="categoryId-field" className="form-label">دسته‌بندی</Label>
                            <Input
                              name="categoryId"
                              type="select"
                              className="form-select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.categoryId || ''}
                              invalid={validation.touched.categoryId && !!validation.errors.categoryId}
                            >
                              <option value="">انتخاب دسته‌بندی</option>
                              {categories.map((cat) => (
                                <option key={cat.id} value={cat.id}>
                                  {cat.name}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>{validation.errors.categoryId}</FormFeedback>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label htmlFor="content-field" className="form-label">محتوا</Label>
                            <EditorText
                              value={validation.values.content || ''}
                              onChange={(data) => validation.setFieldValue('content', data)}
                              onBlur={() => validation.setFieldTouched('content', true)}
                            />
                            {validation.touched.content && validation.errors.content ? (
                              <div className="invalid-feedback d-block">{validation.errors.content}</div>
                            ) : null}
                            <small className="text-muted">
                              محتوا باید حداقل 300 کلمه، شامل کلمات کلیدی مرتبط، سرفصل‌های H2/H3، و ساختارمند باشد.
                            </small>
                          </div>
                        </Col>
                        <Col md={12}>
                          <div className="mb-3">
                            <Label htmlFor="image-field" className="form-label">تصویر</Label>
                            <div className="border rounded p-2 bg-light">
                              <Input
                                name="image"
                                id="image-field"
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                onChange={handleImageChange}
                                invalid={validation.touched.image && !!validation.errors.image}
                                className="form-control"
                              />
                              <FormFeedback>{validation.errors.image}</FormFeedback>
                              <small className="text-muted">
                                فقط فرمت‌های PNG، JPG، یا WebP با حداکثر حجم 300 کیلوبایت مجاز است.
                              </small>
                              {imagePreview && (
                                <div className="mt-2">
                                  <img
                                    src={imagePreview}
                                    alt="پیش‌نمایش"
                                    className="rounded"
                                    style={{
                                      maxWidth: '100%',
                                      maxHeight: '150px',
                                      objectFit: 'contain',
                                      border: '1px solid #e9ecef',
                                      borderRadius: '8px',
                                    }}
                                  />
                                </div>
                              )}
                              {isEdit && !validation.values.image && imagePreview && (
                                <div className="mt-2">
                                  <small className="text-muted">تصویر فعلی حفظ خواهد شد مگر اینکه تصویر جدیدی انتخاب کنید.</small>
                                </div>
                              )}
                            </div>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="metaDescription-field" className="form-label">توضیحات متا</Label>
                            <Input
                              name="metaDescription"
                              id="metaDescription-field"
                              className="form-control"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaDescription || ''}
                              invalid={validation.touched.metaDescription && !!validation.errors.metaDescription}
                            />
                            <FormFeedback>{validation.errors.metaDescription}</FormFeedback>
                            <small className="text-muted">
                              توضیحات متا باید 120-160 کاراکتر، جذاب، و شامل کلمه کلیدی باشد.
                            </small>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="metaTitle-field" className="form-label">عنوان متا</Label>
                            <Input
                              name="metaTitle"
                              id="metaTitle-field"
                              className="form-control"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.metaTitle || ''}
                              invalid={validation.touched.metaTitle && !!validation.errors.metaTitle}
                            />
                            <FormFeedback>{validation.errors.metaTitle}</FormFeedback>
                            <small className="text-muted">
                              عنوان متا باید 50-60 کاراکتر و شامل کلمه کلیدی اصلی باشد.
                            </small>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="slug-field" className="form-label">Slug</Label>
                            <Input
                              name="slug"
                              id="slug-field"
                              className="form-control"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.slug || ''}
                              invalid={validation.touched.slug && !!validation.errors.slug}
                            />
                            <FormFeedback>{validation.errors.slug}</FormFeedback>
                            <small className="text-muted">
                              Slug باید کوتاه، خوانا، شامل کلمه کلیدی، و فقط حروف کوچک، اعداد، و خط تیره باشد.
                            </small>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="author-field" className="form-label">نویسنده</Label>
                            <Input
                              name="author"
                              id="author-field"
                              className="form-control"
                              type="text"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.author || ''}
                              invalid={validation.touched.author && !!validation.errors.author}
                            />
                            <FormFeedback>{validation.errors.author}</FormFeedback>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="typePost-field" className="form-label">نوع پست</Label>
                            <Input
                              name="typePost"
                              id="typePost-field"
                              type="select"
                              className="form-select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.typePost || ''}
                              invalid={validation.touched.typePost && !!validation.errors.typePost}
                            >
                              {typePostOptions.map((option) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </Input>
                            <FormFeedback>{validation.errors.typePost}</FormFeedback>
                          </div>
                        </Col>
                        <Col md={6}>
                          <div className="mb-3">
                            <Label htmlFor="status-field" className="form-label">وضعیت</Label>
                            <Input
                              name="status"
                              type="select"
                              className="form-select"
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.status || ''}
                              invalid={validation.touched.status && !!validation.errors.status}
                            >
                              <option value="Draft">پیش‌نویس</option>
                              <option value="Published">منتشرشده</option>
                              <option value="Archived">آرشیو</option>
                            </Input>
                            <FormFeedback>{validation.errors.status}</FormFeedback>
                          </div>
                        </Col>
                      </Row>
                    </ModalBody>
                    <div className="modal-footer">
                      <div className="hstack gap-2 justify-content-end">
                        <Button type="button" className="btn btn-light" onClick={toggle}>
                          بستن
                        </Button>
                        <Button type="submit" className="btn btn-success" disabled={loading}>
                          {isEdit ? 'به‌روزرسانی' : 'افزودن پست'}
                        </Button>
                      </div>
                    </div>
                  </Form>
                </Modal>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default BlogPosts;